import React from 'react'

import { Box, useTheme } from '@mui/material'

import { useTermSheet } from 'components/UI/TermSheetProvider'
import { glossaryTerm } from 'docs/glossary'

export interface TermProps {
  /** A consumer-glossary entry id. Renders as plain prose if it does not resolve. */
  id: string
  /** The words as they appear in the sentence. Never the id. */
  children: React.ReactNode
}

/**
 * The words as they were authored, flattened to a string.
 *
 * In practice `children` is a bare string — every one of the corpus's `term:` links is
 * written `[one or two plain words](term:<id>)` — but react-markdown types it as a node,
 * and a future author writing `[**verified**](term:...)` would otherwise silently produce
 * an empty accessible name. Recursing costs four lines and removes that class of failure.
 */
const visibleText = (node: React.ReactNode): string =>
  React.Children.toArray(node)
    .map(child => {
      if (typeof child === 'string' || typeof child === 'number') return String(child)
      if (React.isValidElement(child))
        return visibleText((child.props as { children?: React.ReactNode }).children)
      return ''
    })
    .join('')
    .trim()

/**
 * A term of art inside a Help article's prose, carrying its own definition.
 *
 * The consumer half of the platform's `Term` (see that file for why the syntax is a link
 * with a `term:` scheme, and for the react-markdown sanitizer trap). Authors write
 * `[the words they want](term:<id>)` in `docs/help/*.md` and `ArticlePage` routes it here.
 *
 * ── Why this is not the platform's component ─────────────────────────────────────────
 *
 * §47 says improve the shared base rather than rebuild sideways, and what is genuinely
 * shared here IS shared: one `docs/glossary.json`, one publisher, one definition. What
 * differs is the whole trigger mechanism. The platform opens a hover tooltip and, for a
 * fuller entry, a centred dialog; this app has no hover at all — it is a phone, held by
 * someone with the item in their other hand — so a definition opens the bottom `TermSheet`
 * every other `(i)` in this app opens. A shared component would be two implementations
 * behind one name, which is worse than two names.
 *
 * ── Why it looks the way it does ─────────────────────────────────────────────────────
 *
 * Same reasoning as the platform's: a reference is not a destination, so colour stays with
 * the article's real links and this is marked by a dotted rule under the word. The colour
 * is `text.primary` rather than the body's `text.secondary` — the prose here is deliberately
 * set in the secondary tone, and a term that receded further than the sentence around it
 * would not read as an affordance at all.
 *
 * There is no hover state, on purpose. `@media (hover: hover)` guards exist elsewhere in
 * this app for controls that are also used on a desktop preview; a word in a paragraph has
 * one interaction here and it is a tap.
 *
 * ── The accessible name is the AUTHORED words, not the term's title ───────────────────
 *
 * This used to read `aria-label={`${entry.term} — show definition`}`, which REPLACED the
 * words in the sentence with the glossary's own title for the entry. Those two are not
 * always the same string, and the live corpus contains both cases: `[history](term:provenance)`
 * and `[tap](term:scan)`. A reader sees "history"; the accessible name said "Provenance".
 *
 * That is a WCAG 2.5.3 (Label in Name, Level A) failure — the visible label has to be
 * contained in the accessible name — and it breaks speech control concretely: "click
 * history" matches nothing, because the only name the control answers to is a word that
 * appears nowhere on screen. It also mangles the prose for a screen reader, which reads the
 * label in place of the content: "The item's Provenance — show definition is its chain of
 * owners."
 *
 * So the name is now built FROM the authored words, with the affordance kept as a suffix.
 * For the fifteen corpus links whose words already match their entry this is unchanged in
 * all but capitalisation; for the two that do not, it is the fix. The canonical term is not
 * lost either way — the sheet opens with it as the title.
 *
 * ── Unresolved ids ───────────────────────────────────────────────────────────────────
 *
 * This app ships the CONSUMER corpus only, so a brand-floor id resolves to nothing. It then
 * renders as plain prose rather than as a tap target that opens an empty sheet — the same
 * choice `TermInfo` makes and for the same reason. `publish-help.mjs` fails the build on
 * such a reference, so this is a backstop rather than a path anything relies on.
 */
export const Term = ({ id, children }: TermProps) => {
  const theme = useTheme()
  const { openTerm } = useTermSheet()
  const entry = glossaryTerm(id)

  if (!entry) return <>{children}</>

  // Falls back to the entry's title only if the author wrote a link with no text at all,
  // which the publisher would reject — a nameless control is worse than a mismatched one.
  const words = visibleText(children) || entry.term

  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      aria-label={`${words} — show definition`}
      onClick={event => {
        // Articles are reached from rows and sheets that are themselves interactive; the
        // same guard `TermInfo` documents, for the same reason — without it a tap could
        // both open the definition and fire whatever this sits inside.
        event.stopPropagation()
        openTerm(entry.id)
      }}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        event.stopPropagation()
        openTerm(entry.id)
      }}
      sx={{
        cursor: 'pointer',
        color: theme.palette.text.primary,
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.text.primary}`,
          outlineOffset: '2px',
          borderRadius: '2px',
        },
      }}
    >
      {children}
    </Box>
  )
}
